<script lang="ts" setup>
// DsGlobalTooltip — port de GlobalTooltip (primitives.jsx). À monter UNE fois près
// de la racine. Tout `title`/`data-tip` sur n'importe quel élément devient une bulle
// sombre au survol ou au focus (positionnée au-dessus, ou en dessous si pas de place).
//
// Le listener est volontairement global : monté par la branche `chromeV2` de layouts/default.vue,
// il habille aussi des écrans encore legacy (le chrome v2 est fait pour ça, cf. useFeatureVersion).
// Ce sous-arbre ne nous appartient pas, donc rien n'y est muté durablement : `title` est vidé
// le temps de l'affichage — sinon le navigateur superpose sa bulle native, qu'aucune CSS ne peut
// neutraliser — puis RESTAURÉ à la fermeture (même principe que `data-bs-original-title` de
// Bootstrap). `aria-describedby` relie l'ancre à la bulle pendant ce temps, pour que l'information
// portée par `title` reste exposée aux lecteurs d'écran.
//
// NB : n'utilise volontairement PAS useDsAnchoredOverlay. Cet overlay porte un état ancré
// (open/toggle/close, refs anchor/float fixes, clic-extérieur, Échap, repositionnement
// scroll/resize, géométrie de MENU left/width/openUp). Ici l'ancre change à chaque survol
// (mouseover global), il n'y a pas d'état d'ouverture, et la géométrie est centrée x/y au
// curseur — pas une largeur d'ancre. Les mutualiser ajouterait de la complexité des deux côtés ;
// le seul point commun (basculer au-dessus/dessous) tient en deux lignes.
interface TipState { text: string; x: number; y: number; place: 'top' | 'bottom' }

const BUBBLE_ID = 'gd-gtip-bubble';
const ANCHOR_SELECTOR = '[data-tip],[title]';

const tip = ref<TipState | null>(null);
let current: HTMLElement | null = null;
// État à rendre à l'ancre en la relâchant : `title` retiré (null = il n'y en avait pas) et
// `aria-describedby` posé par nous (false = l'ancre en avait déjà un, on n'y touche pas).
let removedTitle: string | null = null;
let addedDescribedBy = false;
// Une ancre qui disparaît sous le curseur n'émet aucun `mouseout` : on la surveille tant qu'une
// bulle est visible. `isConnected` seul, `getClientRects()` étant vide pour tout élément sous happy-dom.
let anchorWatch: number | null = null;
let anchorChanges: MutationObserver | null = null;

function stopWatch (): void {
  if (anchorWatch === null) { return; }
  cancelAnimationFrame(anchorWatch);
  anchorWatch = null;
}

function watchAnchor (): void {
  stopWatch();
  const tick = (): void => {
    anchorWatch = null;
    if (!current) { return; }
    if (!current.isConnected) { hide(); return; }
    anchorWatch = requestAnimationFrame(tick);
  };
  anchorWatch = requestAnimationFrame(tick);
}

function borrowTitle (el: HTMLElement): void {
  const title = el.getAttribute('title');
  if (title === null || title === '') { return; }
  removedTitle = title;
  el.setAttribute('title', '');
}

function followAnchor (): void {
  const el = current;
  if (!el || !tip.value) { return; }
  if (!el.hasAttribute('title')) { removedTitle = null; }
  borrowTitle(el);
  const text = el.getAttribute('data-tip') ?? removedTitle;
  if (!text) { hide(); return; }
  tip.value = { ...tip.value, text };
}

function hide (): void {
  stopWatch();
  anchorChanges?.disconnect();
  anchorChanges = null;
  if (current) {
    if (current.getAttribute('title') === '') {
      if (removedTitle === null) { current.removeAttribute('title'); } else { current.setAttribute('title', removedTitle); }
    }
    if (addedDescribedBy) { current.removeAttribute('aria-describedby'); }
  }
  removedTitle = null;
  addedDescribedBy = false;
  current = null;
  tip.value = null;
}

function show (el: HTMLElement): void {
  // `title` est relu à CHAQUE survol : Vue le réécrit quand le binding change (« Réduire » ⇄
  // « Développer »). Le figer dans `data-tip` laissait revenir la bulle native du navigateur.
  const text = el.getAttribute('data-tip') ?? el.getAttribute('title');
  if (!text) { return; }
  hide(); // relâche l'ancre précédente (title restauré) avant d'en adopter une nouvelle
  current = el;
  borrowTitle(el);
  if (!el.hasAttribute('aria-describedby')) {
    el.setAttribute('aria-describedby', BUBBLE_ID);
    addedDescribedBy = true;
  }
  const r = el.getBoundingClientRect();
  const above = r.top > 60;
  tip.value = {
    text,
    x: r.left + r.width / 2,
    y: above ? r.top - 8 : r.bottom + 8,
    place: above ? 'top' : 'bottom',
  };
  watchAnchor();
  anchorChanges = new MutationObserver(followAnchor);
  anchorChanges.observe(el, { attributes: true, attributeFilter: ['title', 'data-tip'] });
}

function onOver (event: MouseEvent): void {
  const el = (event.target as HTMLElement).closest<HTMLElement>(ANCHOR_SELECTOR);
  if (el && el !== current) { show(el); }
}
function onOut (event: MouseEvent): void {
  if (current && (!event.relatedTarget || !current.contains(event.relatedTarget as Node))) {
    hide();
  }
}
// Parité clavier : la bulle est la seule restitution de `title` tant qu'elle est affichée.
function onFocusIn (event: FocusEvent): void {
  const el = (event.target as HTMLElement | null)?.closest<HTMLElement>(ANCHOR_SELECTOR);
  if (el && el !== current) { show(el); }
}
function onDown (): void { hide(); }

onMounted(() => {
  document.addEventListener('mouseover', onOver);
  document.addEventListener('mouseout', onOut);
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', hide);
  document.addEventListener('mousedown', onDown, true);
});
onUnmounted(() => {
  document.removeEventListener('mouseover', onOver);
  document.removeEventListener('mouseout', onOut);
  document.removeEventListener('focusin', onFocusIn);
  document.removeEventListener('focusout', hide);
  document.removeEventListener('mousedown', onDown, true);
  hide(); // le chrome peut être démonté (bascule de flag) pendant un survol
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="tip"
      :id="BUBBLE_ID"
      :class="['gd-gtip', tip.place]"
      :style="{ left: `${tip.x}px`, top: `${tip.y}px` }"
      role="tooltip"
    >
      {{ tip.text }}
    </div>
  </Teleport>
</template>
